import { signal } from '@preact/signals-react';
import { Button } from 'antd';
import appointmentActions from 'features/appointment/services/actions';
import appointmentSelectors from 'features/appointment/services/selectors';
import { memo, useEffect, useState } from 'react';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { isZoomIn } from './styles';
import { DEFAULT_STAFF_LIMIT_PAGING, DEFAULT_STAFF_LIMIT_PAGING_LEVEL_2 } from 'features/appointment/services/constants';
export const calendarElement = signal<HTMLElement | null>(null);
export const useZoomConfig = () => {
  const [calendar, setCalendar] = useState<HTMLElement | null>(null);
  const [zoomValue, setZoomValue] = useState(1);
  const viewType = appointmentSelectors.getViewType();
  const layout = appointmentSelectors.getAppointmentLayout();
  const dispatch = useAppDispatch();
  const staffLimit = appointmentSelectors.getStaffLimit();

  useEffect(() => {
    const view = document.getElementById('calendar');
    setCalendar(view);
    calendarElement.value = view;
  }, [viewType, layout]);

  useEffect(() => {
    if (!calendar) return;
    // @ts-ignore
    calendar.style.zoom = zoomValue;
  }, [zoomValue]);

  const handleZoomIn = () => {
    if (!calendar) return;
    const nextValue = zoomValue + 0.1;

    isZoomIn.value = false;
    if (nextValue === 1 && staffLimit !== DEFAULT_STAFF_LIMIT_PAGING) {
      dispatch(appointmentActions.setStaffLimit(DEFAULT_STAFF_LIMIT_PAGING));
    }

    if (nextValue > 1) return;
    setZoomValue(nextValue);

  };
  const handleZoomOut = () => {
    if (!calendar) return;
    const nextValue = zoomValue - 0.1;
    if (nextValue < 0.8) return;

    isZoomIn.value = true;

    if (staffLimit !== DEFAULT_STAFF_LIMIT_PAGING_LEVEL_2) {
      dispatch(appointmentActions.setStaffLimit(DEFAULT_STAFF_LIMIT_PAGING_LEVEL_2));
    }
    setZoomValue(nextValue);
  };

  return ({
    handleZoomIn, handleZoomOut, calendar
  });
};

const ZoomConfig = () => {
  const { calendar, handleZoomIn, handleZoomOut } = useZoomConfig();
  if (!calendar) return null;

  return (
    <IconWrapper>
      <IconButton className='arrow-btn' onClick={handleZoomOut}><IconZoomOut /></IconButton>
      <IconButton className='arrow-btn' onClick={handleZoomIn}><IconZoomIn /></IconButton>
    </IconWrapper>
  );
};

export default memo(ZoomConfig);
const IconWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const IconButton = styled(Button)`
  background-color: white;
  border-radius: 50%;
  border: 1px solid black;
  width: 3rem;
  height: 3rem;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
`;

const IconZoomIn = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
    <path fillRule="evenodd" clipRule="evenodd" d="M22.4001 13.6C17.5399 13.6 13.6 17.5399 13.6 22.4001C13.6 27.2602 17.5399 31.2001 22.4001 31.2001C27.2602 31.2001 31.2001 27.2602 31.2001 22.4001C31.2001 17.5399 27.2602 13.6 22.4001 13.6ZM12 22.4001C12 16.6563 16.6563 12 22.4001 12C28.1439 12 32.8001 16.6563 32.8001 22.4001C32.8001 28.1439 28.1439 32.8001 22.4001 32.8001C16.6563 32.8001 12 28.1439 12 22.4001Z" fill="#1D2129" />
    <path fillRule="evenodd" clipRule="evenodd" d="M29.0344 29.0351C29.3468 28.7227 29.8534 28.7227 30.1659 29.0351L35.7665 34.6358C36.079 34.9482 36.079 35.4548 35.7665 35.7673C35.4541 36.0797 34.9475 36.0797 34.635 35.7673L29.0344 30.1666C28.7219 29.8542 28.7219 29.3476 29.0344 29.0351Z" fill="#1D2129" />
    <path fillRule="evenodd" clipRule="evenodd" d="M22.4004 18.3984C22.8422 18.3984 23.2004 18.7567 23.2004 19.1985V21.5988H25.6007C26.0426 21.5988 26.4008 21.957 26.4008 22.3989C26.4008 22.8408 26.0426 23.199 25.6007 23.199H23.2004V25.5993C23.2004 26.0411 22.8422 26.3993 22.4004 26.3993C21.9585 26.3993 21.6003 26.0411 21.6003 25.5993V23.199H19.2C18.7581 23.199 18.3999 22.8408 18.3999 22.3989C18.3999 21.957 18.7581 21.5988 19.2 21.5988H21.6003V19.1985C21.6003 18.7567 21.9585 18.3984 22.4004 18.3984Z" fill="#1D2129" />
  </svg>
);

const IconZoomOut = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
    <path fillRule="evenodd" clipRule="evenodd" d="M22.3997 13.6002C17.5391 13.6002 13.5987 17.5405 13.5987 22.4012C13.5987 27.2618 17.5391 31.2022 22.3997 31.2022C27.2604 31.2022 31.2007 27.2618 31.2007 22.4012C31.2007 17.5405 27.2604 13.6002 22.3997 13.6002ZM11.9985 22.4012C11.9985 16.6568 16.6553 12 22.3997 12C28.1441 12 32.8009 16.6568 32.8009 22.4012C32.8009 28.1456 28.1441 32.8024 22.3997 32.8024C16.6553 32.8024 11.9985 28.1456 11.9985 22.4012Z" fill="#1D2129" />
    <path fillRule="evenodd" clipRule="evenodd" d="M29.0346 29.0351C29.3471 28.7227 29.8537 28.7227 30.1661 29.0351L35.7668 34.6358C36.0792 34.9482 36.0792 35.4548 35.7668 35.7673C35.4543 36.0797 34.9477 36.0797 34.6353 35.7673L29.0346 30.1666C28.7222 29.8542 28.7222 29.3476 29.0346 29.0351Z" fill="#1D2129" />
    <path fillRule="evenodd" clipRule="evenodd" d="M18.3994 22.4017C18.3994 21.9598 18.7576 21.6016 19.1995 21.6016H25.6002C26.0421 21.6016 26.4003 21.9598 26.4003 22.4017C26.4003 22.8435 26.0421 23.2017 25.6002 23.2017H19.1995C18.7576 23.2017 18.3994 22.8435 18.3994 22.4017Z" fill="#1D2129" />
  </svg>
);
